import React, { useEffect, useRef, useState } from "react";
import {
  BriefcaseBusiness,
  Check,
  ChevronDown,
  GraduationCap,
  Languages,
  Link2,
  MapPin,
  Sparkles,
  UserRound,
} from "lucide-react";
import {
  EducationLevel,
  LanguageEnum,
  LocationPreference,
} from "../../../Redux/Portfolio/types";
import { usePortfolioContext } from "./PortfolioContext";

type SelectOption = { label: string; value: string };

type PortfolioSelectProps = {
  value: string;
  placeholder: string;
  options: SelectOption[];
  onChange: (value: string) => void;
};

const PortfolioSelect = ({
  value,
  placeholder,
  options,
  onChange,
}: PortfolioSelectProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? placeholder;

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div className={`portfolio-custom-select ${open ? "open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`portfolio-custom-select-trigger ${value ? "has-value" : ""}`}
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedLabel}</span>
        <ChevronDown size={18} />
      </button>

      {open && (
        <div className="portfolio-custom-select-menu" role="listbox">
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                className={`portfolio-custom-select-option ${selected ? "selected" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                role="option"
                aria-selected={selected}
              >
                <span>{option.label}</span>
                {selected && <Check size={16} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Bio: React.FC = () => {
  const { formData, setFormData } = usePortfolioContext();

  const update = (name: string, value: string) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => update(e.target.name, e.target.value);

  const handleLanguageToggle = (lang: LanguageEnum) => {
    setFormData((prev) => {
      const current = prev.knownLanguages;
      return {
        ...prev,
        knownLanguages: current.includes(lang)
          ? current.filter((l) => l !== lang)
          : [...current, lang],
      };
    });
  };

  const handleLocationToggle = (loc: LocationPreference) => {
    setFormData((prev) => {
      const current = prev.locationPreference;
      return {
        ...prev,
        locationPreference: current.includes(loc)
          ? current.filter((l) => l !== loc)
          : [...current, loc],
      };
    });
  };

  const textField = (
    name: string,
    placeholder: string,
    type: string = "text",
  ) => (
    <input
      type={type}
      name={name}
      value={(formData as any)[name]}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );

  return (
    <div className="portfolio-tab-panel">
      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <UserRound size={20} />
          </div>
          <div>
            <span>PERSONAL INTRODUCTION</span>
            <h2>About Me</h2>
          </div>
        </div>
        <textarea
          name="aboutMe"
          value={formData.aboutMe}
          onChange={handleChange}
          className="portfolio-textarea portfolio-about-textarea"
          placeholder="Tell people about yourself, your interests, personality and what makes your profile unique..."
        />
      </section>

      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <Sparkles size={20} />
          </div>
          <div>
            <span>PROFILE DETAILS</span>
            <h2>Physical Stats</h2>
          </div>
        </div>
        <div className="portfolio-form-grid portfolio-form-grid-three">
          {textField("height", "Height (cm)", "number")}
          {textField("weight", "Weight (kg)", "number")}
          {textField("bust", "Bust (inches)", "number")}
          {textField("waist", "Waist (inches)", "number")}
          {textField("hips", "Hips (inches)", "number")}
          {textField("chest", "Chest (inches)", "number")}
          {textField("biceps", "Biceps (inches)", "number")}
          {textField("hairType", "Hair type")}
          {textField("eyeColor", "Eye color")}
        </div>
      </section>

      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <Languages size={20} />
          </div>
          <div>
            <span>CAPABILITIES</span>
            <h2>Skills & Languages</h2>
          </div>
        </div>
        <div className="portfolio-form-grid portfolio-form-grid-two">
          {textField("skills", "Expertise in (e.g. Acting, Singing)")}
        </div>
        <div className="portfolio-check-grid">
          {Object.values(LanguageEnum).map((lang) => (
            <button
              type="button"
              key={lang}
              className={`portfolio-check-chip ${formData.knownLanguages.includes(lang) ? "selected" : ""}`}
              onClick={() => handleLanguageToggle(lang)}
              aria-pressed={formData.knownLanguages.includes(lang)}
            >
              {lang}
            </button>
          ))}
        </div>
      </section>

      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <BriefcaseBusiness size={20} />
          </div>
          <div>
            <span>PROFESSIONAL PREFERENCE</span>
            <h2>Work Preference</h2>
          </div>
        </div>
        <div className="portfolio-check-grid">
          {Object.values(LocationPreference).map((loc) => (
            <button
              type="button"
              key={loc}
              className={`portfolio-check-chip ${formData.locationPreference.includes(loc) ? "selected" : ""}`}
              onClick={() => handleLocationToggle(loc)}
              aria-pressed={formData.locationPreference.includes(loc)}
            >
              {loc}
            </button>
          ))}
        </div>
      </section>

      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <GraduationCap size={20} />
          </div>
          <div>
            <span>EDUCATION</span>
            <h2>Academic Details</h2>
          </div>
        </div>
        <div className="portfolio-form-grid portfolio-form-grid-two">
          <PortfolioSelect
            value={formData.educationLevel || ""}
            placeholder="Education level"
            options={Object.values(EducationLevel).map((level) => ({
              label: level,
              value: level,
            }))}
            onChange={(value) => update("educationLevel", value)}
          />
          {textField("degree", "Degree / Course name")}
        </div>
      </section>

      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <MapPin size={20} />
          </div>
          <div>
            <span>CONTACT</span>
            <h2>Contact Details</h2>
          </div>
        </div>
        <div className="portfolio-form-grid portfolio-form-grid-two">
          {textField("contactName", "Contact name")}
          {textField("email", "E-mail ID", "email")}
          {textField("mobile", "Mobile number")}
        </div>
      </section>

      <section className="portfolio-form-section">
        <div className="portfolio-section-title">
          <div className="portfolio-section-icon">
            <Link2 size={20} />
          </div>
          <div>
            <span>SOCIAL PRESENCE</span>
            <h2>Social Media Links</h2>
          </div>
        </div>
        <div className="portfolio-form-grid portfolio-form-grid-two">
          {textField("facebook", "Facebook URL")}
          {textField("instagram", "Instagram URL")}
          {textField("twitter", "Twitter / X URL")}
          {textField("youtube", "YouTube URL")}
        </div>
      </section>
    </div>
  );
};

export default Bio;
